<template>
  <div class="main-body" ref="mainBody">
    <div class="title-bar">
      <h1>Company Report</h1>
      <div class="actions">
        <div class="icon-button" @click="printReport">
          <img id="noHover" src="@/assets/print.svg" />
          <img id="hover" src="@/assets/print-hover.svg" />
        </div>
        <div class="icon-button" @click="downloadReport">
          <img id="noHover" src="@/assets/download.svg" />
          <img id="hover" src="@/assets/download-hover.svg" />
        </div>
        <div class="icon-button" @click="showFilter = true">
          <img id="noHover" src="@/assets/filter.svg" />
          <img id="hover" src="@/assets/filter-hover.svg" />
        </div>

        <button :class="{ 'square-btn': true, saving: loading }" :disabled="loading" @click="handleRunReport">Run Report<img src="@/assets/spinner.svg" /></button>
      </div>
    </div>

    <div class="print-title-bar">
      <h1>Company Report</h1>
    </div>

    <div class="scrollable-div display-scrollbar" ref="scrollableDiv" @scroll="handleScroll">
      <table class="full-width-table sortable-table resizeable-table ellipsis-table">
        <tbody>
          <tr class="table-header">
            <th
              v-for="column in sortedColumns"
              :key="column.key"
              :style="{ width: `${column.width || column.flexWidth * 5}%` }"
              @click="handleSort(column.key)"
              :class="{ 'force-show-resize-handle': currentColumn && currentColumn.key === column.key && resizing, 'force-dont-show-resize-handle': currentColumn && currentColumn.key != column.key && resizing }">
              {{ column.title }}
              <img :class="{ 'sort-arrow': true, desc: sortDirection === 'desc' && column.key === sortColumn }" src="@/assets/sort-arrow.svg" />
              <div class="resize-handle" @mousedown.stop="startResize($event, column)" @click.stop=""><span></span></div>
            </th>
          </tr>
          <tr v-if="!loading" v-for="company in sortedReportData" :key="company.COMPANYID">
            <td v-for="column in sortedColumns" :key="column.key">
              <span v-if="column.key === 'NAME'" class="link" @click="navigateToCompany(company)">
                {{ company[column.key] || '' }}
              </span>
              <span v-else-if="column.key === 'WEBADDRESS' && company[column.key]" class="link" @click="openWebsite(company[column.key])">
                {{ company[column.key] || '' }}
              </span>
              <span v-else>{{ company[column.key] || '' }}</span>
            </td>
          </tr>
          <tr v-if="loading" class="loading-row">
            <td :colspan="sortedColumns.length"><img src="@/assets/spinner.svg" /></td>
          </tr>
          <tr v-if="sortedReportData.length === 0 && !loading" class="no-results">
            <td :colspan="sortedColumns.length">No data found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <CompanyReportFilter v-if="showFilter" @closePopup="showFilter = false" />
  </div>
</template>

<script>
import useCompanyReportQuery from '@/composables/useCompanyReportQuery';
import CompanyReportFilter from '@/components/CompanyReportFilter.vue';
import useAlerts from '@/composables/useAlerts';
import { onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'CompanyReport',
  components: {
    CompanyReportFilter,
  },
  setup() {
    const { reportData, loading, runReport, filters, columnsList, validateFilters } = useCompanyReportQuery();
    const { addAlert } = useAlerts();

    const originalTitle = document.title;
    onMounted(() => {
      document.title = 'CMS - Company Report';
    });

    onBeforeUnmount(() => {
      document.title = originalTitle;
    });

    return {
      reportData,
      loading,
      runReport,
      filters,
      columnsList,
      validateFilters,
      addAlert,
    };
  },
  data() {
    return {
      showFilter: false,
      sortColumn: 'NAME',
      sortDirection: 'asc',
      resizing: false,
      currentColumn: null,
      startX: 0,
      startWidth: 0,
      maxRows: 100,
      rowIncrement: 50,
    };
  },
  computed: {
    sortedColumns() {
      return this.filters.columns
        .filter((colName) => this.columnsList.find((col) => col.name === colName))
        .map((colName) => {
          const column = this.columnsList.find((col) => col.name === colName);
          return {
            key: column.name,
            title: column.title,
            flexWidth: column.flexWidth,
            order: column.order,
          };
        })
        .sort((a, b) => a.order - b.order);
    },
    sortedReportData() {
      if (!this.reportData) return [];

      const sortedData = [...this.reportData].sort((a, b) => {
        // Get the values for the primary sort column, convert to strings and handle null/undefined
        let valueA = (a[this.sortColumn] ?? '').toString();
        let valueB = (b[this.sortColumn] ?? '').toString();

        // If either value is empty, place it at the bottom
        if (!valueA && !valueB) return 0;
        if (!valueA) return 1;
        if (!valueB) return -1;

        // Primary sort comparison
        const comparison = valueA.localeCompare(valueB);
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });

      return sortedData.slice(0, this.maxRows);
    },
  },
  methods: {
    downloadReport() {
      if (!this.reportData || !this.reportData.length) {
        this.addAlert('No data to download', 'low', 4);
        return;
      }

      // Create CSV header from column titles
      const headers = this.sortedColumns.map((col) => col.title);

      // Create CSV rows from data
      const rows = this.reportData.map((company) => {
        return this.sortedColumns
          .map((col) => {
            const value = company[col.key];
            return value ? `"${String(value).replace(/"/g, '""')}"` : '';
          })
          .join(',');
      });

      // Combine headers and rows
      const csvContent = [headers.join(','), ...rows].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `company_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    openWebsite(url) {
      // Add protocol if missing
      const website = url.startsWith('http') ? url : `https://${url}`;
      window.open(website, '_blank');
    },
    navigateToCompany(company) {
      if (company.COMPANYID) {
        this.$router.push({ name: 'companies', params: { companyid: company.COMPANYID } });
      }
    },
    handleRunReport() {
      if (!this.validateFilters()) {
        this.showFilter = true;
        return;
      }
      this.maxRows = 100;
      this.runReport();
    },
    handleSort(column) {
      if (this.resizing) return;
      if (this.sortColumn === column) {
        // Toggle sort direction if clicking the same column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Set new sort column and default to ascending
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    handleScroll(event) {
      const element = event.target;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;

      const nearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (nearBottom && this.maxRows < this.reportData.length && !this.loading) {
        this.maxRows += this.rowIncrement;
      }
    },
    startResize(event, column) {
      event.preventDefault();
      this.resizing = true;
      this.currentColumn = column;

      // Find the th element by traversing up the DOM tree
      let columnElement = event.target.parentElement;
      while (columnElement && columnElement.tagName !== 'TH') {
        columnElement = columnElement.parentElement;
      }

      if (!columnElement) {
        console.error('Could not find th element');
        return;
      }

      this.startX = event.pageX;
      this.startWidth = columnElement.offsetWidth;

      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
    },
    handleResize(event) {
      if (!this.resizing) return;

      const deltaX = event.pageX - this.startX;
      const newWidth = this.startWidth + deltaX;
      const tableWidth = this.$el.querySelector('table').offsetWidth;

      // Calculate what percentage 5rem is of the table width
      const minWidthInPixels = 5 * 16; // 5rem = 5 * 16px (assuming 1rem = 16px)
      const minWidthPercentage = (minWidthInPixels / tableWidth) * 100;

      // Ensure the new width is not less than 5rem
      const percentageWidth = Math.max((newWidth / tableWidth) * 100, minWidthPercentage);

      // Update the column width
      this.currentColumn.width = percentageWidth;

      // Force Vue to update the view
      this.$forceUpdate();
    },
    stopResize() {
      setTimeout(() => {
        this.resizing = false;
      }, 10);
      this.currentColumn = null;
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
    },
    printReport() {
      window.print();
    },
  },
  beforeUnmount() {
    // Clean up event listeners
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
  },
};
</script>

<style lang="scss" scoped>
.print-title-bar {
  display: none;
}

@media print {
  @page {
    size: landscape;
    padding: 10px;
  }

  .title-bar {
    display: none;
  }

  .print-title-bar {
    display: block;
    text-align: center;
    margin-bottom: 20px;
    padding: 10px;
    border-bottom: 2px solid #000;

    h1 {
      font-size: 24px;
      margin: 0 0 10px 0;
    }

    .print-details {
      font-size: 12px;
      color: #666;
      margin: 0;

      p {
        margin: 2px 0;
      }
    }
  }

  .scrollable-div {
    height: auto !important;
    page-break-inside: auto;
  }

  table {
    width: 100% !important;
    border-collapse: collapse;
    page-break-inside: auto;
  }

  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    font-size: 12px !important;
    padding-left: 5px !important;
    padding-right: 5px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }

  th {
    background-color: #f5f5f5 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .link {
    text-decoration: none;
    color: black;
  }

  .sort-arrow,
  .resize-handle {
    display: none;
  }
}
</style>
